// components/collection/CollectionComponents.js
import React from 'react';
import { Modal, Container, Card, Form, Button, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CollectionItem } from './CollectionItem';

// ============================================
// Loading Spinner Component
// ============================================
export const LoadingSpinner = () => {
  const { t } = useTranslation('common');
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
      <div 
        className="spinner-border" 
        role="status"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="visually-hidden">{t('common:loading', 'Loading...')}</span>
      </div>
    </div>
  );
};

// ============================================
// Empty Collection Message
// ============================================
const EmptyCollectionMessage = () => {
  const { t } = useTranslation('common');
  return (
    <div className="text-center p-4" style={{ color: 'var(--text-primary)' }}>
      <h4 style={{ fontFamily: 'var(--site-font)', marginBottom: '1rem' }}>{t('common:emptyCollectionTitle', 'Your collection is empty')}</h4>
      <p style={{ color: 'var(--text-tertiary)' }}>{t('common:emptyCollectionMessage', 'Start saving items to see them here!')}</p>
    </div>
  );
};

// ============================================
// Tag Filter Component
// ============================================
export const TagFilter = ({ 
  availableTags, 
  selectedTags, 
  onToggleTag, 
  onClearFilters 
}) => {
  const { t } = useTranslation('common');
  if (!availableTags || availableTags.length === 0) return null;

  return (
    <div style={{ 
      padding: '0.5rem 1rem', 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: '8px',
      alignItems: 'center'
    }}>
      {availableTags.map(tag => (
        <Badge 
          key={tag}
          bg=""
          style={{ 
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            backgroundColor: selectedTags.includes(tag) 
              ? 'var(--bg-button)' 
              : 'var(--bg-button-secondary)',
            border: selectedTags.includes(tag) 
              ? '1px solid var(--border-primary)' 
              : '1px solid transparent',
            color: 'var(--text-primary)'
          }}
          onClick={() => onToggleTag(tag)}
        >
          {tag}
        </Badge>
      ))}
      {selectedTags.length > 0 && (
        <Button 
          variant="link" 
          size="sm" 
          onClick={onClearFilters}
          style={{ 
            color: 'var(--text-tertiary)', 
            textDecoration: 'none',
            fontSize: '12px'
          }}
        >
          {t('common:clearFilters', 'Clear filters')}
        </Button>
      )}
    </div>
  );
};

// ============================================
// Collection Content Component
// ============================================
export const CollectionContent = ({ 
  collection,
  filteredCollection, 
  onEditNote, 
  onRemove,
  onClearFilters
}) => {
  const { t } = useTranslation('common');

  if (filteredCollection.length === 0) {
    if (collection.length === 0) {
      return <EmptyCollectionMessage />;
    } else {
      return (
        <div className="text-center p-4" style={{ color: 'var(--text-primary)' }}>
          <p style={{ marginBottom: '1rem' }}>{t('common:noItemsMatchSearch', 'No items match your search criteria.')}</p>
          <Button 
            variant="outline-light" 
            onClick={onClearFilters}
            style={{
              borderRadius: '20px',
              padding: '8px 20px',
              backgroundColor: 'transparent',
              borderColor: 'var(--border-primary)',
              color: 'var(--text-primary)'
            }}
          >
            {t('common:clearAllFilters', 'Clear all filters')}
          </Button>
        </div>
      );
    }
  }

  return (
    <>
      {filteredCollection.map((item, index) => (
        <CollectionItem
          key={item.id || index}
          item={item}
          index={index}
          onEditNote={onEditNote}
          onRemove={onRemove}
        />
      ))}
    </>
  );
};

// ============================================
// Inline utility function for date formatting
// ============================================
const formatDate = (inputDate) => {
  if (!inputDate) return '';
  
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }
  
  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';
    
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// ============================================
// Edit Note Modal Component
// ============================================
export const EditNoteModal = ({ 
  isOpen, 
  item, 
  note, 
  onNoteChange, 
  onSave, 
  onClose 
}) => {
  const { t } = useTranslation('common');

  if (!item) return null;

  const metadataFields = [item.feedName, item.branch, item.topic]
    .filter(field => field && field !== 'null' && field.trim() !== '');
  const metadataString = metadataFields.join(' | ');

  return (
    <>
      <style>{`
        #editNoteModal .modal-content {
          background-color: var(--modal-bg);
          border: none;
          border-radius: 18px;
        }
        
        #editNoteModalHeader {
          background-color: var(--modal-header-bg);
          border-bottom: 1px solid var(--border-light);
          border-radius: 18px 18px 0 0;
        }
        
        #editNoteModalHeader h3 {
          color: var(--text-primary);
          font-family: var(--site-font);
          font-size: 1.25rem;
          margin: 0;
        }
        
        #editNoteModalHeader .btn-close {
          filter: var(--close-btn-filter, invert(1));
          opacity: 0.7;
        }
        
        [data-theme="light"] #editNoteModalHeader .btn-close {
          filter: none;
        }
        
        #editNoteModalHeader .btn-close:hover {
          opacity: 1;
        }
        
        #editNoteModalBody {
          background-color: var(--modal-bg);
          padding: 0;
          border-radius: 0 0 18px 18px;
        }
        
        #editNoteContainer {
          padding: 20px;
          border-radius: 20px;
          background-color: var(--bg-card-solid);
          box-shadow: 10px 10px 10px var(--shadow-color);
        }
        
        #editNoteContainer .card-title {
          font-size: 16px;
          font-family: var(--site-font);
          color: var(--text-primary);
          margin-bottom: 0.75rem;
        }
        
        #editNoteContainer .card-body {
          font-size: 14px;
          font-family: var(--site-font);
          color: var(--text-secondary);
          padding: 0;
          margin-bottom: 1rem;
        }
        
        #editNoteForm {
          padding: 0;
          margin-bottom: 1rem;
        }
        
        #editNoteTextarea {
          width: 100%;
          background-color: var(--input-bg);
          color: var(--text-primary);
          border: 1px solid var(--border-light);
          border-radius: 10px;
          padding: 12px;
          font-family: var(--site-font);
          font-size: 14px;
          resize: vertical;
          min-height: 100px;
        }
        
        #editNoteTextarea::placeholder {
          color: var(--text-muted);
        }
        
        #editNoteTextarea:focus {
          background-color: var(--input-bg);
          color: var(--text-primary);
          border-color: var(--border-primary);
          box-shadow: 0 0 0 0.2rem var(--accent-glow);
          outline: none;
        }
        
        #saveNoteButton,
        #cancelNoteButton {
          padding: 8px 20px;
          background-color: var(--bg-button);
          color: var(--text-primary);
          border: none;
          border-radius: 20px;
          box-shadow: 2px 2px 12px var(--shadow-color);
          font-family: var(--site-font);
          transition: all 0.2s ease;
        }
        
        #saveNoteButton:hover,
        #cancelNoteButton:hover {
          background-color: var(--bg-button-hover);
          transform: translateY(-1px);
        }
        
        #cancelNoteButton {
          margin-right: 10px;
        }
      `}</style>
      
      <Modal
        id="editNoteModal"
        show={isOpen}
        size="lg"
        aria-labelledby="edit-note-modal"
        centered
        onHide={onClose}
      >
        <Modal.Header id="editNoteModalHeader" closeButton>
          <h3>{t('common:editNote', 'Edit Note')}</h3>
        </Modal.Header>
        <Modal.Body id="editNoteModalBody">
          <Container id="editNoteContainer">
            <Card.Title>{item.title}</Card.Title>
            <Card.Body>
              {metadataString && <div>{metadataString}</div>}
              {item.date && <div>{formatDate(item.date)}</div>}
            </Card.Body>
            <Form id="editNoteForm">
              <Form.Group>
                <Form.Control 
                  id="editNoteTextarea"
                  as="textarea"
                  rows={3}
                  placeholder={t('common:addNotePlaceholder', 'Add your note here...')}
                  value={note}
                  onChange={(e) => onNoteChange(e.target.value)}
                />
              </Form.Group>
            </Form>
            <div className="d-flex justify-content-end">
              <Button id="cancelNoteButton" onClick={onClose}>
                {t('common:cancel', 'Cancel')}
              </Button>
              <Button id="saveNoteButton" onClick={onSave}>
                {t('common:saveNote', 'Save Note')}
              </Button>
            </div>
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default {
  LoadingSpinner,
  TagFilter,
  CollectionContent,
  EditNoteModal
};